import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "../../../../../components/Button";
import { ModalCloseButton } from "../../../../../components/ModalCloseButton";
import type {
  ProspectResponse,
  MergeProspectsResponse,
} from "../../../../../types/api";
import {
  mergeProspects,
  buildExpectedVersions,
  MergeError,
} from "../../../../../services/prospects";
import {
  computeConflicts,
  defaultChoices,
  buildResolvedFields,
  pickDefaultSurvivor,
  prospectName,
  type FieldChoices,
} from "./mergeFields";
import { mergeErrorMessage, shouldRefetchAfter } from "./mergeErrors";
import { SurvivorStep } from "./SurvivorStep";
import { ConflictStep } from "./ConflictStep";
import { PreviewStep } from "./PreviewStep";
import "./MergeProspectsModal.css";

interface MergeProspectsModalProps {
  isOpen: boolean;
  /** The full prospect records for the selected ids (>= 2). */
  prospects: ProspectResponse[];
  /**
   * Pre-select this record as the survivor instead of the most-complete default.
   * The Merge Manager passes the oldest record, which is the one its group card
   * promised would survive.
   */
  preferredSurvivorId?: string;
  onClose: () => void;
  /**
   * Called after a successful merge. Receives the survivor id and the list of
   * loser ids the parent should drop from its cache/search before routing to
   * the survivor.
   */
  onSuccess: (survivorId: string, mergedIds: string[], result: MergeProspectsResponse) => void;
  /**
   * Called when the error indicates the local snapshot is stale so the parent
   * can re-fetch the player list. The modal stays open with the error shown.
   */
  onStaleData?: () => void;
}

type Step = "survivor" | "conflicts" | "preview";

const STEP_ORDER: Step[] = ["survivor", "conflicts", "preview"];

export const MergeProspectsModal = ({
  isOpen,
  prospects,
  preferredSurvivorId,
  onClose,
  onSuccess,
  onStaleData,
}: MergeProspectsModalProps) => {
  const [step, setStep] = useState<Step>("survivor");
  const [survivorId, setSurvivorId] = useState<string>("");
  const [choices, setChoices] = useState<FieldChoices>({});
  // Records the coach has taken back out of this merge. Duplicate detection is a
  // guess, so a group can pick up someone who is genuinely a different player;
  // excluding them here leaves their profile untouched.
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Synchronous latch against double-submit: the `isSubmitting` state read inside
  // handleConfirm is a stale closure value, so a ref is the reliable guard for an
  // irreversible action (the disabled button is a second line of defense).
  const inFlightRef = useRef(false);
  // The Idempotency-Key for the CURRENT merge attempt. Reused across retries of
  // the same resolved payload so a lost-response retry replays the original
  // outcome server-side instead of risking a double-merge. Cleared whenever the
  // payload changes (different survivor/choices = a genuinely different merge).
  const idempotencyKeyRef = useRef<string>("");
  const dialogRef = useRef<HTMLDivElement>(null);
  // The element focused before the modal opened, restored on close so keyboard
  // users return to the trigger.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Reset wizard state whenever the modal opens with a fresh selection.
  useEffect(() => {
    if (isOpen) {
      const defaultSurvivor = prospects.length > 0 ? pickDefaultSurvivor(prospects) : "";
      const seeded = prospects.some((p) => p.id === preferredSurvivorId)
        ? (preferredSurvivorId as string)
        : defaultSurvivor;
      setStep("survivor");
      setSurvivorId(seeded);
      setExcludedIds(new Set());
      setChoices({});
      setIsSubmitting(false);
      setError("");
      inFlightRef.current = false;
      idempotencyKeyRef.current = "";
    }
    // We intentionally key only on `isOpen` so re-renders from parent state
    // (e.g. a background refetch) don't wipe in-progress choices.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const survivor = useMemo(
    () => prospects.find((p) => p.id === survivorId) ?? null,
    [prospects, survivorId]
  );
  // Only included non-survivors are merged. Everything downstream — conflicts,
  // preview, the submitted payload — derives from this, so an excluded record is
  // never touched by the merge.
  const losers = useMemo(
    () => prospects.filter((p) => p.id !== survivorId && !excludedIds.has(p.id)),
    [prospects, survivorId, excludedIds]
  );

  /** Picking a survivor also puts it back in the merge if it was excluded. */
  const handleSelectSurvivor = useCallback((prospectId: string) => {
    setSurvivorId(prospectId);
    setExcludedIds((current) => {
      if (!current.has(prospectId)) return current;
      const next = new Set(current);
      next.delete(prospectId);
      return next;
    });
  }, []);

  const handleToggleInclude = useCallback((prospectId: string) => {
    setExcludedIds((current) => {
      const next = new Set(current);
      if (next.has(prospectId)) next.delete(prospectId);
      else next.add(prospectId);
      return next;
    });
  }, []);

  const conflicts = useMemo(() => {
    if (!survivor) return [];
    return computeConflicts(survivor, losers);
  }, [survivor, losers]);

  // Seed choices to survivor-wins defaults whenever the survivor (and therefore
  // the conflict set) changes. Switching survivor invalidates prior picks.
  useEffect(() => {
    setChoices(defaultChoices(conflicts));
  }, [conflicts]);

  // A change to the survivor or the resolved choices means the next confirm is a
  // genuinely different merge — invalidate the current idempotency key so we
  // don't replay a stale attempt's outcome onto a new payload.
  useEffect(() => {
    idempotencyKeyRef.current = "";
  }, [survivorId, choices, excludedIds]);

  // ESC closes the modal unless a merge is in flight (don't orphan the request).
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, isSubmitting, onClose]);

  // Move focus into the dialog on open and restore it to the trigger on close,
  // so keyboard / screen-reader users aren't left on the underlying page during
  // this irreversible flow.
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && !isSubmitting) onClose();
    },
    [isSubmitting, onClose]
  );

  const handleChoiceChange = useCallback((conflictKey: string, prospectId: string) => {
    setChoices((prev) => ({ ...prev, [conflictKey]: prospectId }));
  }, []);

  const goNext = () => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx < STEP_ORDER.length - 1) setStep(STEP_ORDER[idx + 1]);
  };
  const goBack = () => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) setStep(STEP_ORDER[idx - 1]);
  };

  const handleConfirm = useCallback(async () => {
    // Synchronous latch — never enters twice for the same payload, even if the
    // disabled-button guard is bypassed (Enter + click, programmatic calls).
    if (!survivor || losers.length === 0 || inFlightRef.current) return;
    inFlightRef.current = true;

    setIsSubmitting(true);
    setError("");

    const resolvedFields = buildResolvedFields(survivor, losers, conflicts, choices);
    const expectedVersions = buildExpectedVersions([survivor, ...losers]);
    const mergedIds = losers.map((p) => p.id);

    // Mint a key on the first attempt; reuse it on retries of the same payload.
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }

    try {
      const { result } = await mergeProspects(
        {
          survivingProspectId: survivor.id,
          mergedProspectIds: mergedIds,
          expectedVersions,
          resolvedFields,
        },
        idempotencyKeyRef.current
      );
      // Trust the server's authoritative list of retired profiles for cache
      // eviction rather than the client's (they normally match).
      onSuccess(result.survivingProspectId, result.mergedProspectIds, result);
    } catch (err) {
      setError(mergeErrorMessage(err, prospects));
      // On a stale-snapshot error, ask the parent to re-fetch. We keep ALL
      // profiles visible/unchanged — no optimistic removal happens here.
      if (err instanceof MergeError && shouldRefetchAfter(err.code)) {
        onStaleData?.();
      }
    } finally {
      inFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [survivor, losers, conflicts, choices, prospects, onSuccess, onStaleData]);

  if (!isOpen || prospects.length < 2 || !survivor) return null;

  const stepNumber = STEP_ORDER.indexOf(step) + 1;

  return (
    <div className="merge-modal__overlay" onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="merge-modal__content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-modal-title"
      >
        <div className="merge-modal__header">
          <div>
            <h3 id="merge-modal-title" className="merge-modal__title">
              Merge Players
            </h3>
            <p className="merge-modal__subtitle">
              Step {stepNumber} of {STEP_ORDER.length} ·{" "}
              {/* Say "3 of 5" only once something has been left out, so the common
                  case reads the way it always has. */}
              {losers.length + 1 < prospects.length
                ? `${losers.length + 1} of ${prospects.length} players merging`
                : `${prospects.length} players selected`}
            </p>
          </div>
          <ModalCloseButton onClick={onClose} disabled={isSubmitting} />
        </div>

        <div className="merge-modal__body">
          {step === "survivor" && (
            <SurvivorStep
              prospects={prospects}
              survivorId={survivorId}
              onSelect={handleSelectSurvivor}
              excludedIds={excludedIds}
              onToggleInclude={handleToggleInclude}
            />
          )}
          {step === "conflicts" && (
            <ConflictStep
              survivor={survivor}
              losers={losers}
              conflicts={conflicts}
              choices={choices}
              onChoiceChange={handleChoiceChange}
            />
          )}
          {step === "preview" && (
            <PreviewStep survivor={survivor} losers={losers} />
          )}

          {error && (
            <div className="merge-modal__error" role="alert" data-testid="merge-error">
              {error}
            </div>
          )}
        </div>

        <div className="merge-modal__footer">
          {step === "survivor" ? (
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          ) : (
            <Button variant="secondary" onClick={goBack} disabled={isSubmitting}>
              Back
            </Button>
          )}

          {step === "preview" ? (
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isSubmitting}
              data-testid="merge-confirm-button"
            >
              {isSubmitting
                ? "Merging..."
                : `Merge into ${prospectName(survivor)}`}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={goNext}
              // Excluding everyone but the survivor leaves nothing to merge.
              disabled={losers.length === 0}
              data-testid="merge-next-button"
            >
              {step === "survivor" ? "Next: Resolve conflicts" : "Next: Review"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
